import { FC, useEffect } from 'react';
import { DarwinCollapse } from 'components/darwin-ui';
import Icon, { IconName } from 'components/darwin-ui/Icon';
import styles from './index.module.css';
import { useGetFdaProcesses } from 'api/services/processesService';
import { Process, ProcessCategory } from 'types/processesTypes';
import { useTranslation } from 'react-i18next';
import { useFloatNotification } from 'contexts/FloatNotificationContext';
import { CollapseItem } from './CollapseItem';

export interface DataItem {
  key: string;
  label: string;
  icon: IconName;
}

interface CategoriesCollapseProps {
  selectedKey?: DataItem;
  onSelect: (key: DataItem) => void;
}

const parseCategories = (categories: ProcessCategory[]) => {
  return categories.map((category: ProcessCategory) => ({
    key: category.categoryId,
    label: category.categoryName,
    icon: category.icon as IconName,
    children: category.processes.map((process: Process) => ({
      key: process.id,
      label: process.name,
      icon: process.icon as IconName,
    })),
  }));
};

export const CategoriesCollapse: FC<CategoriesCollapseProps> = ({
  selectedKey,
  onSelect,
}) => {
  const { t } = useTranslation();
  const { openNotification } = useFloatNotification();

  const { data: categoriesData, isError } = useGetFdaProcesses();
  const categories = parseCategories(categoriesData?.data || []);

  // Select the first process of the first category by default.
  useEffect(() => {
    if (categories.length && !selectedKey && categories[0].children.length) {
      onSelect(categories[0].children[0]);
    }
  }, [categories, selectedKey, onSelect]);

  useEffect(() => {
    if (isError) {
      openNotification(t('error'), t('data-tables.categories-error'), 'error');
    }
  }, [isError, t, openNotification]);

  const collapseItems = categories.map((category, index) => ({
    key: index,
    label: (
      <CollapseItem icon={category.icon} label={category.label} isHeader />
    ),
    children: category.children.map(child => (
      <CollapseItem
        key={child.key}
        icon={child.icon}
        label={child.label}
        selected={selectedKey?.key === child.key}
        onClick={() => onSelect(child)}
      />
    )),
  }));

  return categories ? (
    <DarwinCollapse
      accordion
      bordered={false}
      defaultActiveKey={[0]}
      className={styles.dataTableCollapse}
      expandIconPosition="end"
      expandIcon={({ isActive }) => (
        <Icon
          name="chevron-up"
          style={isActive ? { rotate: '180deg' } : { rotate: '0deg' }}
        />
      )}
      items={collapseItems}
    />
  ) : null;
};
